import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';

import { XMarkIcon } from '@heroicons/react/20/solid';

import { dateFormatter } from '@/utils/dateFormatter';

import type { GroupSeatData, GroupSubscriptionData } from '../groupSubscriptionTypes';
import { SeatSearchInput } from './SeatSearchInput';

type SelectMembersToRemoveAtRenewalModalProps = {
  isOpen: boolean;
  group: GroupSubscriptionData | null;
  /** The seat count the subscription will renew at (the reduction target). */
  targetSeatCount: number;
  /** Exact number of members that must be selected for removal at renewal. */
  deficit: number;
  /**
   * Candidate pool — the seats eligible to be scheduled for removal. The host
   * passes the group's filled seats (plus already-terminating seats in
   * edit/swap mode). Empty while the host's fetch is in flight.
   */
  seats: GroupSeatData[];
  /** Pre-checked seat IDs — used in edit/swap mode to seed the currently
   *  terminating seats so the admin edits an existing selection. */
  initialSelectedIds?: string[];
  isLoadingSeats?: boolean;
  /** Back returns to Manage seats; the picker is always reachable from there. */
  onBack: () => void;
  onConfirm: (seatIds: string[]) => void;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
};

/**
 * Picker for choosing which members lose access when seats are reduced below the
 * filled count. Enforces an EXACT `deficit` selection to mirror the backend
 * (`ScheduleSeatReduction` rejects `seat_ids.length != deficit`). Reused for the
 * edit/swap flow by seeding `initialSelectedIds` with the terminating seats.
 *
 * Validation is surfaced on confirm-attempt (not by disabling the button) so the
 * admin gets a reason — matching the Figma "user tries to confirm" states.
 */
export function SelectMembersToRemoveAtRenewalModal({
  isOpen,
  group,
  targetSeatCount,
  deficit,
  seats,
  initialSelectedIds,
  isLoadingSeats = false,
  onBack,
  onConfirm,
  onOpenChange,
  isLoading = false,
}: SelectMembersToRemoveAtRenewalModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(initialSelectedIds ?? []));
  const [search, setSearch] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  // Stable value-key for the pre-selected set. The host fetches candidates
  // asynchronously, so `initialSelectedIds` is often empty on open and resolves
  // a tick later — keying on the sorted ids lets us re-seed once it arrives
  // without re-seeding on every array-identity change (which would wipe toggles).
  const initialIdsKey = [...(initialSelectedIds ?? [])].sort().join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-seed on open, target change, or when the pre-selected set resolves — NOT on every initialSelectedIds identity.
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set(initialSelectedIds ?? []));
      setSearch('');
      setShowValidation(false);
    }
  }, [isOpen, targetSeatCount, initialIdsKey]);

  // ESC closes; outside-click intentionally unbound so a stray click can't wipe
  // a multi-row selection (same pattern as the other group-sub modals).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onOpenChange]);

  const normalizedQuery = search.trim().toLowerCase();
  const visibleSeats = useMemo(
    () =>
      normalizedQuery === '' ? seats : seats.filter((s) => s.email?.toLowerCase().includes(normalizedQuery) ?? false),
    [seats, normalizedQuery],
  );

  const selectedCount = selectedIds.size;
  const allVisibleSelected = visibleSeats.length > 0 && visibleSeats.every((s) => selectedIds.has(s.id));
  const someVisibleSelected = visibleSeats.some((s) => selectedIds.has(s.id));

  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  if (!isOpen || !group) return null;

  // Force UTC — renews_at is a calendar date stored as UTC midnight.
  const renewsOnLabel = group.renews_at ? dateFormatter({ timeZone: 'UTC' }).format(new Date(group.renews_at)) : null;
  const memberWord = deficit === 1 ? 'member' : 'members';
  const isExactlyDeficit = selectedCount === deficit;

  const toggleSeat = (seatId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(seatId)) next.delete(seatId);
      else next.add(seatId);
      return next;
    });
    setShowValidation(false);
  };

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const s of visibleSeats) next.delete(s.id);
      } else {
        for (const s of visibleSeats) next.add(s.id);
      }
      return next;
    });
    setShowValidation(false);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setShowValidation(false);
  };

  const handleConfirm = () => {
    if (!isExactlyDeficit) {
      setShowValidation(true);
      return;
    }
    onConfirm([...selectedIds]);
  };

  const overlayStyle: CSSProperties = { backgroundColor: '#00000030' };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="select-members-title"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={overlayStyle}
    >
      <div className="flex max-h-[90vh] w-full flex-col gap-4 overflow-hidden bg-white p-6 shadow-lg sm:w-[600px] sm:rounded-md">
        <div className="flex flex-col gap-2">
          <h1 id="select-members-title" className="text-lg font-medium text-gray-900">
            Select members to remove at renewal
          </h1>
          <p className="text-sm text-gray-500">
            Your subscription will renew with {targetSeatCount} {targetSeatCount === 1 ? 'seat' : 'seats'}
            {renewsOnLabel ? ` on ${renewsOnLabel}` : ''}. Select {deficit} {memberWord} to remove at renewal. They keep
            access until then.
          </p>
        </div>

        <SeatSearchInput value={search} onChange={setSearch} />

        {showValidation && !isExactlyDeficit && (
          <p className="text-sm font-medium text-red-600">
            Select {deficit} {memberWord} to continue
          </p>
        )}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200">
          <div className="flex h-[54px] flex-shrink-0 items-center gap-3 border-b border-gray-200 bg-gray-50 px-4">
            <input
              ref={headerCheckboxRef}
              type="checkbox"
              className={`h-4 w-4 rounded border-gray-400 ${
                allVisibleSelected ? '!bg-wt-primary !text-wt-text-on-primary' : ''
              }`}
              checked={allVisibleSelected}
              onChange={toggleAllVisible}
              aria-label="Select all visible members"
            />
            <span
              className={`inline-flex items-center gap-2 rounded-md border bg-white px-2 py-1 text-sm shadow-sm ${
                selectedCount > deficit ? 'border-red-300 text-red-700' : 'border-gray-200 text-gray-900'
              }`}
            >
              {selectedCount} of {deficit} selected
              {selectedCount > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Clear selection"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoadingSeats && visibleSeats.length === 0 ? (
              <div className="flex h-16 items-center justify-center px-4 py-3 text-sm text-gray-500">Loading…</div>
            ) : visibleSeats.length === 0 ? (
              <div className="flex h-16 items-center justify-center px-4 py-3 text-sm text-gray-500">
                {normalizedQuery ? 'No members match your search.' : 'No members available to remove.'}
              </div>
            ) : (
              visibleSeats.map((seat) => {
                const isSelected = selectedIds.has(seat.id);
                return (
                  <div
                    key={seat.id}
                    className={`flex h-16 items-center border-b border-gray-200 last:border-b-0 ${
                      isSelected ? 'bg-gray-50' : ''
                    }`}
                  >
                    <div className="flex h-full flex-1 items-center gap-3 px-4 py-3">
                      <input
                        type="checkbox"
                        className={`h-4 w-4 rounded border-gray-400 ${
                          isSelected ? '!bg-wt-primary !text-wt-text-on-primary' : ''
                        }`}
                        checked={isSelected}
                        onChange={() => toggleSeat(seat.id)}
                        aria-label={`Select ${seat.email ?? 'member'}`}
                      />
                      <span className="flex-1 truncate text-sm font-medium text-gray-900">{seat.email}</span>
                    </div>
                    <div className="flex h-full items-center px-4 py-3">
                      <span className="text-sm text-gray-900">
                        {dateFormatter({}).format(new Date(seat.created_at))}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <p className="text-xs text-gray-500">You can update this list any time before renewal.</p>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            disabled={isLoading}
            onClick={onBack}
            className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Back
          </button>
          <button
            type="button"
            disabled={isLoading}
            onClick={handleConfirm}
            className="rounded-md bg-wt-primary px-3 py-2 text-sm font-medium text-wt-text-on-primary shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:hover:opacity-100"
          >
            Confirm changes
          </button>
        </div>
      </div>
    </div>
  );
}
